import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { RootState } from "../store/store";

export interface MenuQueryFilter
{
   /** Query param name the menu row pinned the page to, from its `query_filter`. */
   param?: string;
   /** The raw entries behind it — offer them as filter options. */
   entries: any[];
   /** Their resolved values (ids), i.e. the unfiltered default. */
   values: any[];
   /** Ready to spread into a request; empty when the row pins no filter. */
   params: Record<string, any>;
}

const EMPTY: MenuQueryFilter = { entries: [], values: [], params: {} };

const collectItems = (config: any): any[] =>
   (config?.menu ?? []).flatMap((group: any) => group?.items ?? []);

/**
 * Finds the menu row that opened the current page and returns the filter it pinned
 * there — the param name, the entries behind it, and the ready-to-spread params
 * layoutServices resolved from the row's `query_filter`:
 *
 *   getOrderAsync({ page, limit, ...menuFilter.params })
 *
 * `paths` overrides the pathnames a row may be matched by, for pages reachable under
 * more than one route.
 *
 * Returns `undefined` while the menu is still loading — a page that filters on this
 * should hold its first request until then, otherwise it would fetch unfiltered.
 */
export default function useMenuQueryFilter(
   paths?: string[],
): MenuQueryFilter | undefined
{
   const location = useLocation();

   const sideMenuConfig = useSelector(
      (state: RootState) => state.layoutSlice.sideMenuConfig,
   );
   const sideBottomMenuConfig = useSelector(
      (state: RootState) => state.layoutSlice.sideBottomMenuConfig,
   );

   const targets = (paths?.length ? paths : [location.pathname]).filter(Boolean);

   return useMemo(() =>
   {
      if (sideMenuConfig === undefined && sideBottomMenuConfig === undefined)
      {
         return undefined;
      }

      const item = [
         ...collectItems(sideMenuConfig),
         ...collectItems(sideBottomMenuConfig),
      ].find(
         (entry: any) =>
            targets.includes(entry?.route) || targets.includes(entry?.path),
      );

      return item?.queryFilter ?? EMPTY;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sideMenuConfig, sideBottomMenuConfig, targets.join("|")]);
}
